<!--
  THE JOVII MARK, in the interface.

  This used to draw the pre-rebrand play-triangle badge (the old `jovi-mark.svg`, since deleted,
  a pink rounded box with a play glyph) — the owner's words: "the logo you put
  all over the UI is the old one". The old SVG is left on disk for the favicon
  pipeline until that is regenerated; nothing in the interface reads it now.

  The mark is the same artwork as the intro (`/brand/intro/w-*.webp`): the
  folded crimson J and, when there is room, the O V I I beside it — laid out
  from the geometry measured off the locked artwork, so this IS the wordmark
  and not an approximation of it in a web font. The table comes from
  `wordmark.ts`, the one module every drawer of the word shares.

  J OR JOVII IS DECIDED BY SPACE, NOT BY A PROP. The default `wordmark="auto"`
  measures the element's own container with a ResizeObserver and shows the full
  word only when the container is at least as wide as the word would be at
  this size. A phone header at 320px gets the J; a desktop bar gets JOVII; a
  window being dragged narrower flips between them live. Callers can still
  force either with `wordmark={true|false}` where the layout is known.

  Every letter is `currentColor`-agnostic — it is an image, in the brand
  crimson, on any ground. That is deliberate: the mark is artwork and does not
  recolour with the theme, exactly as the design sheet says.
-->
<script lang="ts">
  import { INTRO_KEY } from './intro';
  import { J, LETTERS, WORD_ASPECT, glyphSrc } from './wordmark';

  interface Props {
    /** Height of the mark in px. The word scales from it. */
    size?: number;
    /** `true` = always JOVII, `false` = always the J, `'auto'` = whichever fits. */
    wordmark?: boolean | 'auto';
    /** Wrap in a link. Omit for a decorative mark inside another control. */
    href?: string | undefined;
    /** Accessible name when it IS a link. */
    label?: string;
  }

  let { size = 26, wordmark = 'auto', href, label = 'Jovii home' }: Props = $props();

  const wordW = $derived(size * WORD_ASPECT);
  const jW = $derived(size * (J.w / J.h) * WORD_ASPECT * J.h);

  let host = $state<HTMLElement | null>(null);
  let available = $state(Infinity);

  $effect(() => {
    if (wordmark !== 'auto' || !host || typeof ResizeObserver === 'undefined') return;
    const parent = host.parentElement;
    if (!parent) return;
    const ro = new ResizeObserver(([e]) => {
      available = e?.contentRect.width ?? Infinity;
    });
    ro.observe(parent);
    return () => ro.disconnect();
  });

  /* A little slack so the word does not flap at the exact boundary. */
  const showWord = $derived(wordmark === true || (wordmark === 'auto' && available >= wordW * 1.08));
  const width = $derived(showWord ? wordW : jW);

  const asset = (glyph: string): string => glyphSrc(glyph, INTRO_KEY);
</script>

{#snippet drawing()}
  <span class="mark" style:width="{width}px" style:height="{size}px" aria-hidden="true">
    {#each showWord ? LETTERS : [J] as L (L.n)}
      <img
        src={asset(L.glyph)}
        alt=""
        decoding="async"
        style:left="{L.x * wordW}px"
        style:top="{L.y * size}px"
        style:width="{L.w * wordW}px"
        style:height="{L.h * size}px"
      />
    {/each}
  </span>
{/snippet}

{#if href}
  <a class="logo" {href} aria-label={label} bind:this={host}>{@render drawing()}</a>
{:else}
  <span class="logo" role="img" aria-label="Jovii" bind:this={host}>{@render drawing()}</span>
{/if}

<style>
  .logo {
    display: inline-flex;
    align-items: center;
    flex: none;
    line-height: 1;
  }
  .mark {
    position: relative;
    display: block;
    flex: none;
    transition: width var(--dur) var(--ease);
  }
  .mark img {
    position: absolute;
    display: block;
    /* Light's ground is the brand wine, and the crimson artwork sits on it at
       about 2:1. The mark is artwork and does not recolour with the theme, so
       on Light it gets a soft ivory lift under it instead - a halo, not an
       outline - which brings the mark back to legible without touching it. */
    filter: var(--logo-lift, none);
    /* app.css sets `img { max-width: 100% }`; the letters are positioned
       inside a box that is exactly the word's width, so the cap is harmless
       here, but a zero-width ancestor would collapse them — see Intro.svelte. */
    max-width: none;
  }

  a.logo {
    /* Defect #16: a link to Home on every screen gets the 44px floor. */
    min-height: var(--hit);
    min-width: var(--hit);
    justify-content: center;
    padding-inline: var(--space-1);
    margin-inline: calc(var(--space-1) * -1);
    border-radius: var(--radius-pill);
    transition: opacity var(--dur-fast) var(--ease);
  }
  a.logo:hover {
    opacity: 0.82;
  }
  a.logo:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 4px;
  }
</style>